import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createVehicleClaim,
  listVehicleClaims,
  listVehicleTypes,
  getVehicleClaim,
  updateVehicleClaim,
} from '@/lib/vehicle-claims/api';
import type { VehicleClaimCreateRequest, VehicleClaimListFilters } from '@/types/vehicle-claim';

export const vehicleClaimKeys = {
  all:     ['vehicle-claims'] as const,
  lists:   () => [...vehicleClaimKeys.all, 'list'] as const,
  list:    (params: VehicleClaimListFilters & { limit: number; offset: number }) =>
    [...vehicleClaimKeys.lists(), params] as const,
  detail:  (jobId: string) => [...vehicleClaimKeys.all, 'detail', jobId] as const,
};

export function useVehicleClaimList(params: VehicleClaimListFilters & { limit: number; offset: number }) {
  return useQuery({
    queryKey: vehicleClaimKeys.list(params),
    queryFn:  () => listVehicleClaims(params),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/** Active vehicle-type lookup options (Two-Wheeler, Four-Wheeler, ...), for
 * the claims list filter and the Work Order tab dropdown. */
export function useVehicleTypes() {
  return useQuery({
    queryKey: ['vehicle-types'] as const,
    queryFn:  () => listVehicleTypes(),
    staleTime: 60_000,
  });
}

export function useVehicleClaimDetail(jobId: string) {
  return useQuery({
    queryKey: vehicleClaimKeys.detail(jobId),
    queryFn:  () => getVehicleClaim(jobId),
    enabled:  Boolean(jobId),
  });
}

export function useCreateVehicleClaim(options?: { onSuccess?: (data: any) => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: VehicleClaimCreateRequest) => createVehicleClaim(payload),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: vehicleClaimKeys.lists() });
      options?.onSuccess?.(data);
    },
  });
}

export function useUpdateVehicleClaim(jobId: string, options?: { onSuccess?: () => void }, tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => updateVehicleClaim(jobId, payload, tenantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleClaimKeys.detail(jobId) });
      qc.invalidateQueries({ queryKey: vehicleClaimKeys.lists() });
      options?.onSuccess?.();
    },
  });
}
